// Import necessary libraries and components
import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  message,
  Badge,
  Tooltip,
  Modal,
  Checkbox,
} from 'antd';
import {
  CalendarOutlined,
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  LoadingOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import moment from 'moment';

// Import custom components and configurations
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { TaskDatePickerModal } from './TaskDatePickerModal';
import { TaskStartDatePickerModal } from './TaskStartDatePickerModal';
import TaskCodeSnippetModal from './TaskCodeSnippetModal';
import TaskRelatedCampaignsModal from './TaskRelatedCampaignsModal';
// Import custom styles
import '../styles/DatePickerStyles.css';
import '../styles/SelectInput.css';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { useUserContext } from '../../../../core/components/users/useUserContext';
import { uploadFileToS3 } from '../../../shared/upload/uploadFileToS3';
import {
  taskTypes,
  subTypes,
  urgencyTypes,
  urgencyColors,
  allowedFileExtensions,
  allowedImageExtensions,
} from '../configs/task.config';
import { hasCampaignsFeature } from '../utils/task.utils';
import { useParams, useLocation } from 'react-router-dom';
import OverlaySpinner from '../../../shared/OverlaySpinner';
import { getFileType } from '../../../../core/utils/constants/constants';
import { startCase } from 'lodash';
import FileDropPasteArea from '../utils/FileDropPasteArea';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

const { Text } = Typography;
const { Option } = Select;

const TaskForm = ({ history }) => {
  const { id: currentTaskId } = useParams(); // Extract the ID from the URL with alias as currentTaskId
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const linkedProjectId = queryParams.get('projectId');
  const linkedCampaignId = queryParams.get('campaignId');

  const [form] = Form.useForm();
  const textareaRef = useRef(null);
  const {
    currentUser,
    createTask,
    fetchTask,
    currentTask,
    taskLoading,
    updateTask,
    updateTaskLoading,
    fetchCampaignForTask,
    linkedCampaign,
    linkedCampaignLoading,
    setLinkedCampaign,
  } = useTaskContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();
  const { fetchProject, currentProject, fetchProjectsPaginated, projectsConnectionData } = useProjectContext();

  const [initialAttachedFiles, setInitialAttachedFiles] = useState([]);
  const [initialAttachedImages, setInitialAttachedImages] = useState([]);
  const [initialRelatedCampaigns, setInitialRelatedCampaigns] = useState([]);
  const [newAttachedFiles, setNewAttachedFiles] = useState([]);
  const [newAttachedImages, setNewAttachedImages] = useState([]);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [createMoreTasks, setCreateMoreTasks] = useState(false);

  useEffect(() => {
    if (currentTask) {
      setInitialAttachedFiles(currentTask.attachedFiles || []);
      setInitialAttachedImages(currentTask.attachedImages || []);
      setInitialRelatedCampaigns(currentTask.relatedCampaigns || []);
    }
  }, [currentTask]);

  const [assigneeOrgs, setAssigneeOrgs] = useState([]);
  const [channelPartnerOrgs, setChannelPartnerOrgs] = useState([]);
  const [advertiserOrgs, setAdvertiserOrgs] = useState([]);
  const [assigneeUsers, setAssigneeUsers] = useState(basicUsersDetails || []);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(linkedProjectId || null);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);
  const [orgTypeRequest, setOrgTypeRequest] = useState('');
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState(null);
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);
  const windowWidth = useWindowWidth();

  const userOrgName = currentUser && currentUser.role && currentUser.role.org.name;
  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;
  const isChannelPartnerOrg = level === 'CHANNEL_PARTNER';

  const hasCampaigns = hasCampaignsFeature(currentUser);

  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(true);
  const [taskDetailVisible, setTaskDatePickerVisible] = useState(false);

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [attachedImages, setAttachedImages] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const [showCodeSnippetModal, setShowCodeSnippetModal] = useState(false);
  const [attachedCodeSnippet, setAttachedCodeSnippet] = useState('');

  const [showRelatedCampaignModal, setRelatedCampaignModal] = useState(false);
  const [relatedCampaigns, setRelatedCampaigns] = useState([]);

  const [dueDateRecurringConfig, setDueDateRecurringConfig] = useState({});
  const [taskStartDatePickerVisible, setTaskStartDatePickerVisible] = useState(false);

  const handleSubTypeChange = (value) => {
    const subTypesForSelected = subTypes[value] || [];
    setFilteredSubTypes(subTypesForSelected);
    setIsSubTypeDisabled(subTypesForSelected.length === 0);
    form.setFieldsValue({ requestSubType: '' });
  };

  useEffect(() => {
    if (currentTaskId && currentTask) {
      const subTypesForSelected = subTypes[currentTask.requestType] || [];
      setFilteredSubTypes(subTypesForSelected);
      setIsSubTypeDisabled(subTypesForSelected.length === 0);
    }
  }, [currentTaskId, currentTask]);

  const handleInsertCode = () => {
    const textarea =
      textareaRef.current &&
      textareaRef.current.resizableTextArea &&
      textareaRef.current.resizableTextArea.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('taskDetails') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;

    if (selectedText) {
      // Wrap selected text with code blocks
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      // Insert empty code block
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    // Update form value
    form.setFieldsValue({ taskDetails: newValue });

    // Set cursor position after update
    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const insertText = (options) => {
    const textarea =
      textareaRef.current &&
      textareaRef.current.resizableTextArea &&
      textareaRef.current.resizableTextArea.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;

    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);

    form.setFieldsValue({ taskDetails: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = textareaRef.current.resizableTextArea.textArea;
      const value = form.getFieldValue('taskDetails');
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3;
        } else {
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ taskDetails: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleUploadFilesToS3 = async (files) => {
    try {
      const medias = [];
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const media = await uploadFileToS3({ file });
          medias.push({
            ...media,
            fileType: getFileType(file.name),
          });
        }
      }
      return medias;
    } catch (error) {
      console.error(error);
      throw new Error('File Size Exceeded: No greater than 2MB is allowed');
    }
  };

  const handleFileDelete = (fileId, type) => {
    if (type === 'file') {
      setAttachedFiles(attachedFiles.filter((file) => file.id !== fileId));
      setNewAttachedFiles(newAttachedFiles.filter((file) => file.id !== fileId));
    } else if (type === 'image') {
      setAttachedImages(attachedImages.filter((image) => image.id !== fileId));
      setNewAttachedImages(newAttachedImages.filter((image) => image.id !== fileId));
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (errorFields) {
      throw errorFields;
    }

    const {
      name,
      startDate,
      dueDate,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      assigneeOrgId,
      assigneeUserId: selectedAssignees,
      advertiserId,
      taskDetails,
      relatedCampaigns,
      linkedProjectId: selectedProjectId,
    } = form.getFieldsValue();

    let channelPartnerId = form.getFieldValue('channelPartnerId');

    const {
      recurring,
      recurrenceType,
      dateOfMonth,
      dayOfMonth,
      dayOfWeek,
      monthlyType,
      weeklyDays,
      yearlyDate,
      yearlyMonth,
    } = dueDateRecurringConfig;

    const createdBy = {
      connect: {
        id: currentUser.id,
      },
    };

    // Use initial files to detect deletions
    const filesToDelete = initialAttachedFiles.filter(
      (file) => !attachedFiles.some((currentFile) => currentFile.id === file.id)
    );

    const imagesToDelete = initialAttachedImages.filter(
      (image) => !attachedImages.some((currentImage) => currentImage.id === image.id)
    );

    const campaignsToDelete = initialRelatedCampaigns.filter(
      (campaign) => !(relatedCampaigns || []).some((currentCampaign) => currentCampaign.id === campaign.id)
    );

    // Use separate state for tracking new files
    let mediaFiles = [];
    let mediaImages = [];

    try {
      if (currentTaskId && currentTaskId !== 'new') {
        // Check and upload only new files
        if (newAttachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(newAttachedFiles);
        }

        if (newAttachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(newAttachedImages);
        }
      } else {
        if (attachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(attachedFiles);
        }
        if (attachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(attachedImages);
        }
      }
    } catch (error) {
      message.error(error.message);
      return;
    }

    // Determine channel partner ID if not set
    if (!channelPartnerId && isChannelPartnerOrg) {
      channelPartnerId = currentUser.role.org.id;
    }

    const assigneeUserIds = selectedAssignees;
    const previousAssigneeUsers =
      currentTask && currentTask.assigneeUsers ? currentTask.assigneeUsers : [];
    const previousAssigneeUsersIds = previousAssigneeUsers.map((user) => user.id);

    const assigneeUsers = {};
    const collaborators =
      currentTaskId && currentTask && currentTask.collaborators
        ? currentTask.collaborators.map((user) => user.id)
        : [];
    const followers =
      currentTaskId && currentTask && currentTask.followers
        ? currentTask.followers.map((user) => user.id)
        : [];

    const assigneeIdToCheck = Array.isArray(assigneeUserIds)
      ? assigneeUserIds[0]?.key || assigneeUserIds[0]
      : assigneeUserIds;

    if (
      currentTaskId &&
      assigneeIdToCheck &&
      collaborators.includes(assigneeIdToCheck) &&
      assigneeUserIds.length > 0
    ) {
      message.warning(
        'Assignee is already a collaborator. Please remove them from collaborators first.'
      );
      return;
    }

    if (
      currentTaskId &&
      assigneeIdToCheck &&
      followers.includes(assigneeIdToCheck) &&
      assigneeUserIds.length > 0
    ) {
      message.warning('Assignee is already a follower. Please remove them from followers first.');
      return;
    }

    if (currentTaskId && currentTaskId !== 'new') {
      if (assigneeUserIds && previousAssigneeUsers && !assigneeUserIds[0].key) {
        const usersToDisconnect = previousAssigneeUsersIds.filter(
          (id) => !assigneeUserIds.includes(id)
        );
        if (usersToDisconnect.length > 0) {
          assigneeUsers.disconnect = usersToDisconnect.map((id) => ({ id }));
        }
      }

      if (
        assigneeUserIds &&
        !previousAssigneeUsersIds.includes(assigneeUserIds) &&
        !assigneeUserIds[0].key
      ) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    } else {
      if (assigneeUserIds && assigneeUserIds.length > 0 && !assigneeUserIds[0]?.key) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    }

    // Build the main task data object
    let data = {
      name,
      startDate,
      dueDate,
      createdBy,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      assigneeOrg: selectedProjectId ? {} : (assigneeOrgId ? { connect: { id: assigneeOrgId } } : {}),
      assigneeUsers,
      channelPartner: channelPartnerId ? { connect: { id: channelPartnerId } } : {},
      advertiser: advertiserId ? { connect: { id: advertiserId } } : {},
      attachedFiles: mediaFiles && mediaFiles.length ? { create: mediaFiles } : {},
      attachedImages: mediaImages && mediaImages.length ? { create: mediaImages } : {},
      relatedCampaigns:
        relatedCampaigns && relatedCampaigns.length
          ? {
              connect: relatedCampaigns.map((campaign) => ({
                id: campaign.id,
              })),
            }
          : undefined, // Ensure related campaigns are included if any
      taskDetails,
      codeSnippet: attachedCodeSnippet,
      status: 'NOT_STARTED',
      project: selectedProjectId ? { connect: { id: selectedProjectId } } : {},
    };

    // Add recurring data, resetting the data object if it is already stored in the GQL
    if (recurring) {
      data = { ...data, recurring, recurrenceType };
      switch (recurrenceType) {
        case 'DAILY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'MONTHLY':
          data =
            monthlyType === 'DATE_OF_MONTH'
              ? {
                  ...data,
                  dateOfMonth,
                  monthlyType,
                  weeklyDays: { set: null },
                  dayOfMonth: null,
                  dayOfWeek: null,
                  yearlyDate: null,
                  yearlyMonth: null,
                }
              : {
                  ...data,
                  dayOfMonth,
                  dayOfWeek: dayOfWeek.toUpperCase(),
                  monthlyType,
                  dateOfMonth: null,
                  weeklyDays: { set: null },
                  yearlyDate: null,
                  yearlyMonth: null,
                };
          break;
        case 'WEEKLY':
          data = {
            ...data,
            weeklyDays: { set: weeklyDays },
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'BIWEEKLY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'YEARLY':
          data = {
            ...data,
            yearlyDate,
            yearlyMonth,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
          };
          break;
        default:
          break;
      }
    }

    try {
      message.loading(`${currentTaskId && currentTaskId !== 'new' ? 'Updating' : 'Creating'} task...`, 0);

      if (currentTaskId && currentTaskId !== 'new') {
        // Delete files that have been removed by the user
        if (filesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: {
                attachedFiles: {
                  delete: filesToDelete.map((file) => ({ id: file.id })),
                },
              },
            },
          });
        }

        if (imagesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: {
                attachedImages: {
                  delete: imagesToDelete.map((image) => ({ id: image.id })),
                },
              },
            },
          });
        }

        if (campaignsToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: {
                relatedCampaigns: {
                  disconnect: campaignsToDelete.map((campaign) => ({
                    id: campaign.id,
                  })),
                },
              },
            },
          });
        }

        // Filter out the data that is not needed for the update
        const { status, createdBy, ...filteredData } = data;

        // Proceed with the main update including new files
        await updateTask({
          variables: {
            where: { id: currentTaskId },
            data: filteredData,
          },
        });
        history.push(`/tasks/details/${currentTaskId}`);
      } else {
        await createTask({ variables: { data } });

        if (createMoreTasks) {
          const currentValues = form.getFieldsValue();
          form.setFieldsValue({
            ...currentValues,
            name: '',
          });
          setAttachedFiles([]);
          setAttachedImages([]);
          setNewAttachedFiles([]);
          setNewAttachedImages([]);
          setAttachedCodeSnippet('');

          window.scrollTo({ top: 0, behavior: 'smooth' });

          message.destroy();
          message.success('Task created successfully. Create another task below.');
        } else {
          if (history) {
            if (selectedProjectId) {
              history.push(`/tasks/projects/details/${selectedProjectId}`);
            } else {
              history.push(`/tasks/main/1`);
            }
          }
          message.destroy();
          message.success('Task created successfully');
        }
      }

      if (currentTaskId && currentTaskId !== 'new') {
        message.destroy();
        message.success('Task updated successfully');
      }
    } catch (error) {
      message.destroy();
      message.error(`Failed to ${currentTaskId && currentTaskId !== 'new' ? 'update' : 'create'} task: ${error.message}`);
      console.error('Task submission error:', error);
    }
  };

  const fetchBasicOrgs = debounce((name = '') => {
    setOrgTypeRequest('ASSIGNEE');
    fetchBasicOrgsDetails({
      variables: {
        first: 10,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                { parentOrg_some: { id: currentUser.role.org.id } },
                { id: currentUser.role.org.id },
              ],
              name_contains: name,
            },
          ],
          OR: [
            {
              roles_some: {
                org: {
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
            },
          ],
        },
      },
    });
  }, 500);

  const fetchChannelPartnerOrgs = debounce((name = '') => {
    setOrgTypeRequest('CHANNEL_PARTNER');
    fetchBasicOrgsDetails({
      variables: {
        first: 300,
        skip: 0,
        where: {
          AND: [
            {
              level_in: ['CHANNEL_PARTNER'],
            },
            {
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchAdvertiserOrgs = debounce((name = '') => {
    const channelPartnerId =
      level !== 'PLATFORM' ? currentUser.role.org.id : form.getFieldValue('channelPartnerId');
    if (!channelPartnerId) {
      setAdvertiserOrgs([]);
      return;
    }
    setOrgTypeRequest('ADVERTISER');
    fetchBasicOrgsDetails({
      variables: {
        first: 1000,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                {
                  parentOrg_some: {
                    id: channelPartnerId,
                  },
                },
                {
                  id: channelPartnerId,
                },
              ],
            },
            {
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchBasicUsers = debounce((name = '') => {
    const assigneeOrgId = form.getFieldValue('assigneeOrgId');
    if (!assigneeOrgId) {
      setAssigneeUsers([]);
      return;
    }
    setOrgTypeRequest('CHANNEL_PARTNER');
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: assigneeOrgId,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchProjects = debounce((name = '') => {
    fetchProjectsPaginated({
      variables: {
        first: 100,
        skip: 0,
        where: {
          name_contains: name,
          owner: {
            roles_some: {
              org: {
                OR: [
                  { id: currentUser?.role?.org?.id },
                ],
              },
            },
          },
        },
      },
    });
  }, 500);

  useEffect(() => {
    if (currentTaskId) {
      fetchTask({
        variables: {
          where: {
            id: currentTaskId,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (linkedProjectId && currentTaskId === 'new') {
      console.log('Setting linkedProjectId from URL:', linkedProjectId);

      fetchProject({
        variables: {
          where: {
            id: linkedProjectId,
          },
        },
      });

      form.setFieldsValue({
        linkedProjectId: linkedProjectId,
      });

      console.log('Form values after set:', form.getFieldsValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedProjectId]);

  useEffect(() => {
    if (linkedCampaignId && currentTaskId === 'new') {
      fetchCampaignForTask({
        variables: {
          id: linkedCampaignId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaignId]);

  useEffect(() => {
    if (linkedCampaign && currentTaskId === 'new' && linkedCampaignId) {
      const formattedCampaign = {
        id: linkedCampaign.id,
        name: linkedCampaign.name,
        startDate: moment(linkedCampaign.startDate),
        endDate: moment(linkedCampaign.endDate),
      };
      setRelatedCampaigns([formattedCampaign]);
      form.setFieldsValue({ relatedCampaigns: [formattedCampaign] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaign]);

  // Cleanup linked campaign when unmounting
  useEffect(() => {
    return () => {
      setLinkedCampaign(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (linkedProjectId && currentProject && currentTaskId === 'new') {
      setProjects((prev) => {
        const exists = prev.find((p) => p.id === linkedProjectId);
        if (exists) return prev;
        return [currentProject, ...prev];
      });

      setSelectedProjectId(linkedProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, linkedProjectId]);

  useEffect(() => {
    const formProjectId = form.getFieldValue('linkedProjectId');
    if (formProjectId) {
      setSelectedProjectId(formProjectId);

      const selectedProject = projects.find(p => p.id === formProjectId) || currentProject;
      if (selectedProject) {
        const teamMembers = [];
        if (selectedProject.owner) {
          teamMembers.push(selectedProject.owner);
        }
        if (selectedProject.team && selectedProject.team.length > 0) {
          teamMembers.push(...selectedProject.team);
        }
        setProjectTeamMembers(teamMembers);
      }
    } else {
      setSelectedProjectId(null);
      setProjectTeamMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('linkedProjectId'), projects, currentProject]);

  useEffect(() => {
    switch (orgTypeRequest) {
      case 'ASSIGNEE':
        setAssigneeOrgs(basicOrgsDetails);
        break;
      case 'CHANNEL_PARTNER':
        setChannelPartnerOrgs(basicOrgsDetails);
        break;

      case 'ADVERTISER':
        setAdvertiserOrgs(basicOrgsDetails);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicOrgsDetails]);

  useEffect(() => {
    setAssigneeUsers(basicUsersDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicUsersDetails]);

  useEffect(() => {
    if (projectsConnectionData && projectsConnectionData.projectsConnection) {
      setProjects(projectsConnectionData.projectsConnection.edges.map((edge) => edge.node));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsConnectionData]);

  useEffect(() => {
    if (currentTaskId && currentTask && form) {
      form.setFieldsValue({
        name: currentTask.name,
        startDate: currentTask.startDate ? moment(currentTask.startDate) : null,
        dueDate: currentTask.dueDate ? moment(currentTask.dueDate) : null,
        requestType: currentTask.requestType,
        requestSubType: currentTask.requestSubType,
        linkUrl: currentTask.linkUrl,
        urgency: currentTask.urgency,
        assigneeOrgId: currentTask.assigneeOrg ? currentTask.assigneeOrg.id : null,
        assigneeUserId:
          currentTask.assigneeUsers && currentTask.assigneeUsers.length > 0
            ? currentTask.assigneeUsers.map((user) => ({
                key: user.id,
                label: startCase(user.name || user.username),
              }))
            : currentTask.assigneeUser
              ? currentTask.assigneeUser.id
              : undefined,
        channelPartnerId: currentTask.channelPartner ? currentTask.channelPartner.id : null,
        advertiserId: currentTask.advertiser ? currentTask.advertiser.id : null,
        taskDetails: currentTask.taskDetails,
        relatedCampaigns:
          currentTask.relatedCampaigns && currentTask.relatedCampaigns.length
            ? currentTask.relatedCampaigns.map((rCampaign) => ({
                ...rCampaign,
                startDate: moment(rCampaign.startDate),
                endDate: moment(rCampaign.endDate),
              }))
            : [],
        linkedProjectId: currentTask.project ? currentTask.project.id : null,
      });

      setRelatedCampaigns(
        currentTask.relatedCampaigns && currentTask.relatedCampaigns.length
          ? currentTask.relatedCampaigns.map((rCampaign) => ({
              ...rCampaign,
              startDate: moment(rCampaign.startDate),
              endDate: moment(rCampaign.endDate),
            }))
          : []
      );

      setAssigneeOrgs(
        assigneeOrgs.concat(currentTask.assigneeOrg ? [currentTask.assigneeOrg] : [])
      );
      setChannelPartnerOrgs(
        channelPartnerOrgs.concat(currentTask.channelPartner ? [currentTask.channelPartner] : [])
      );
      setAdvertiserOrgs(
        advertiserOrgs.concat(currentTask.advertiser ? [currentTask.advertiser] : [])
      );
      setAssigneeUsers(
        assigneeUsers.concat(currentTask.assigneeUser ? [currentTask.assigneeUser] : [])
      );
      setProjects(
        projects.concat(currentTask.project ? [currentTask.project] : [])
      );
      setAttachedFiles(currentTask.attachedFiles || []);
      setAttachedImages(currentTask.attachedImages || []);
      setAttachedCodeSnippet(currentTask.codeSnippet || '');
      setDueDateRecurringConfig({
        recurring: currentTask.recurring,
        recurrenceType: currentTask.recurrenceType,
        dateOfMonth: currentTask.dateOfMonth,
        dayOfMonth: currentTask.dayOfMonth,
        dayOfWeek: currentTask.dayOfWeek,
        monthlyType: currentTask.monthlyType,
        weeklyDays: currentTask.weeklyDays,
        yearlyDate: currentTask.yearlyDate,
        yearlyMonth: currentTask.yearlyMonth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  if (taskLoading) {
    return <OverlaySpinner />;
  }
  document.title = currentTaskId ? 'Update Task' : 'Create Task';
  return (
    <>
      <PageHeaderLayout
        title={currentTaskId && currentTaskId !== 'new' ? 'Update Task' : 'Create Task'}
        style={{
          fontSize: windowWidth < 768 ? '18px' : '22px',
          display: 'flex',
          border: 'none',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          paddingTop: '2vh',
        }}
      />
      <PageContentLayout
        style={{
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          width: '100%',
          height: '100%',
        }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          {linkedProjectId && currentProject && currentTaskId === 'new' && (
            <Row style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Card
                  style={{
                    backgroundColor: '#e0f2fe',
                    borderRadius: '7px',
                    border: '1px solid #7dd3fc',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: '14px', color: '#0369a1' }}>
                      <strong>Linked Project:</strong> {currentProject.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}
          {linkedCampaignId && linkedCampaign && currentTaskId === 'new' && (
            <Row style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Card
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '7px',
                    border: '1px solid #93c5fd',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: '14px', color: '#1e40af' }}>
                      <strong>Linked Campaign:</strong> {linkedCampaign.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}
          {/* TASK DETAILS */}
          <Row>
            <Col style={{ width: '100%', marginBlock: '3vh' }}>
              <Card
                style={{ borderRadius: '7px', backgroundColor: '#f9fafb' }}
                title={<Text style={{ fontSize: '18px', fontWeight: 'bold' }}>TASK DETAILS</Text>}
                headStyle={{ borderBottom: 'none' }}
              >
                <Row gutter={windowWidth < 768 ? 16 : 32}>
                  <Col xs={24} sm={24} md={level === 'PLATFORM' ? 12 : 12} lg={level === 'PLATFORM' ? 12 : 12}>
                    <Form.Item
                      name="name"
                      label={
                        <div>
                          <div>Task Name</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            The name you will identify this task by
                          </div>
                        </div>
                      }
                      rules={[{ required: true, message: 'Task Name is Required' }]}
                      colon={false}
                    >
                      <Input placeholder="Enter task name" className="custom-input" />
                    </Form.Item>
                  </Col>
                  {level !== 'PLATFORM' && (
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="dueDate"
                        label={
                          <div>
                            <div>Due Date</div>
                            <div style={{ color: 'gray', fontSize: '12px' }}>
                              Date of task completion
                            </div>
                          </div>
                        }
                        colon={false}
                        layout="vertical"
                        style={{ width: '100%' }}
                        rules={[
                          {
                            required: !selectedProjectId,
                            message: 'Due Date is Required',
                          },
                        ]}
                      >
                        {/* CUSTOM STYLE DATEPICKER */}
                        <DatePicker
                          onOpenChange={(open) => {
                            if (open) {
                              setTaskDatePickerVisible(true); // Open the modal when DatePicker is clicked
                            }
                          }}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                          }}
                          open={false}
                          className="custom-input gray-theme"
                          suffixIcon={<CalendarOutlined />}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                {level === 'PLATFORM' && (
                  <Row gutter={32}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="startDate"
                        label={
                          <div>
                            <div>Start Date</div>
                            <div style={{ color: 'gray', fontSize: '12px' }}>
                              Optional: Date when task starts
                            </div>
                          </div>
                        }
                        colon={false}
                        layout="vertical"
                        style={{ width: '100%' }}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const dueDate = getFieldValue('dueDate');
                              if (!value || !dueDate) {
                                return Promise.resolve();
                              }
                              const startMoment = moment(value);
                              const dueMoment = moment(dueDate);

                              if (startMoment.isSame(dueMoment, 'day')) {
                                return Promise.reject(new Error('Start date cannot be the same as due date'));
                              }
                              if (startMoment.isAfter(dueMoment)) {
                                return Promise.reject(new Error('Start date must be before due date'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          onOpenChange={(open) => {
                            if (open) {
                              setTaskStartDatePickerVisible(true); // Open the modal when DatePicker is clicked
                            }
                          }}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                          }}
                          open={false}
                          className="custom-input gray-theme"
                          suffixIcon={<CalendarOutlined />}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="dueDate"
                        label={
                          <div>
                            <div>Due Date</div>
                            <div style={{ color: 'gray', fontSize: '12px' }}>
                              {selectedProjectId && 'Optional: '}Date of task completion
                            </div>
                          </div>
                        }
                        colon={false}
                        layout="vertical"
                        style={{ width: '100%' }}
                        rules={[
                          {
                            required: !selectedProjectId,
                            message: 'Due Date is Required',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const startDate = getFieldValue('startDate');
                              if (!startDate || !value) {
                                return Promise.resolve();
                              }
                              const startMoment = moment(startDate);
                              const dueMoment = moment(value);

                              if (dueMoment.isSame(startMoment, 'day')) {
                                return Promise.reject(new Error('Due date cannot be the same as start date'));
                              }
                              if (dueMoment.isBefore(startMoment)) {
                                return Promise.reject(new Error('Due date must be after start date'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        {/* CUSTOM STYLE DATEPICKER */}
                        <DatePicker
                          onOpenChange={(open) => {
                            if (open) {
                              setTaskDatePickerVisible(true); // Open the modal when DatePicker is clicked
                            }
                          }}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                          }}
                          open={false}
                          className="custom-input gray-theme"
                          suffixIcon={<CalendarOutlined />}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                <Row gutter={32}>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      name="requestType"
                      label={
                        <div>
                          <div>Request Type</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Select the type that best matches this task
                          </div>
                        </div>
                      }
                      rules={[{ required: true, message: 'Request Type is Required' }]}
                      colon={false}
                    >
                      <Select
                        placeholder="Select request type"
                        onChange={handleSubTypeChange}
                        className="custom-select"
                      >
                        {taskTypes.map((type, index) => (
                          <Option key={index} value={type}>
                            {type}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      name="requestSubType"
                      colon={false}
                      label={
                        <div>
                          <div>Request Details</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Select a sub-type that best matches this task
                          </div>
                        </div>
                      }
                    >
                      <Select
                        placeholder="Select request details"
                        disabled={isSubTypeDisabled}
                        className="custom-select"
                      >
                        {filteredSubTypes.map((subtype, index) => (
                          <Option key={index} value={subtype}>
                            {subtype}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={32}>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      name="linkUrl"
                      label={
                        <div>
                          <div>Link</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Attach one link to the task
                          </div>
                        </div>
                      }
                      colon={false}
                      rules={[
                        {
                          max: 499,
                          message: 'Cannot be any longer than 500 characters',
                        },
                      ]}
                    >
                      <Input placeholder="Enter link" maxLength={500} className="custom-input" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      name="urgency"
                      label={
                        <div>
                          <div>Urgency</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Set the urgency or priority level of this task
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <Select placeholder="Select urgency type" className="custom-select">
                        {Object.keys(urgencyTypes).map((urgency) => (
                          <Option key={urgency} value={urgencyTypes[urgency]}>
                            <Badge
                              color={urgencyColors[urgencyTypes[urgency]]}
                              style={{ marginRight: 8 }}
                            />
                            {urgency}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                {level === 'PLATFORM' && (
                  <Row gutter={32}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="linkedProjectId"
                        label={
                          <div>
                            <div>Linked Project</div>
                            <div style={{ color: 'gray', fontSize: '12px' }}>
                              Optional: Link this task to a project
                            </div>
                          </div>
                        }
                        colon={false}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select a project"
                          disabled={linkedProjectId && currentTaskId === 'new'}
                          onFocus={() => fetchProjects()}
                          onSearch={(searchText) => fetchProjects(searchText)}
                          onChange={(value) => {
                            setSelectedProjectId(value);
                            // Clear assignee when project changes
                            form.setFieldsValue({ assigneeUserId: [] });
                            // Clear due date when linked to a project
                            if (value) {
                              form.setFieldsValue({ dueDate: null });
                            }
                            // Trigger validation update for due date field
                            form.validateFields(['dueDate']);
                          }}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          className="custom-select"
                        >
                          {projects.map((project) => (
                            <Option key={project.id} value={project.id}>
                              {project.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {selectedProjectId && (
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item
                          name="assigneeUserId"
                          label={
                            <div>
                              <div>Assignee</div>
                              <div style={{ color: 'gray', fontSize: '12px' }}>
                                Select from project owner and team members
                              </div>
                            </div>
                          }
                          colon={false}
                        >
                          <Select
                            placeholder="Select Assignee"
                            showSearch
                            filterOption={(input, option) =>
                              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                              optionA.children
                                .toLowerCase()
                                .localeCompare(optionB.children.toLowerCase())
                            }
                            className="custom-select"
                          >
                            {projectTeamMembers.map((user) => (
                              <Option key={user.id} value={user.id}>
                                {startCase(user.name || user.username)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}
              </Card>
            </Col>

            {/* REQUEST DETAILS */}
            <Col style={{ width: '100%' }}>
              <Card
                style={{ borderRadius: '7px', backgroundColor: '#f9fafb' }}
                title={
                  <Text style={{ fontSize: '18px', fontWeight: 'bold' }}>REQUEST DETAILS</Text>
                }
                headStyle={{ borderBottom: 'none' }}
              >
                <Row gutter={32}>
                  {level === 'PLATFORM' && !selectedProjectId && (
                    <>
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item
                          name="assigneeOrgId"
                          label={
                            <div>
                              <div>Assignee Org</div>
                              <div style={{ color: 'gray', fontSize: '12px' }}>
                                The org that will receive this task
                              </div>
                            </div>
                          }
                          colon={false}
                        >
                          <Select
                            allowClear
                            showSearch
                            placeholder="Select Assignee Org"
                            loading={basicOrgsDetailsLoading}
                            onFocus={() => fetchBasicOrgs()}
                            onSearch={(searchText) => fetchBasicOrgs(searchText)}
                            filterOption={(input, option) =>
                              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                              optionA.children
                                .toLowerCase()
                                .localeCompare(optionB.children.toLowerCase())
                            }
                            onChange={() => {
                              form.setFieldsValue({ assigneeUserId: [] });
                            }}
                            className="custom-select"
                          >
                            {assigneeOrgs.map((org) => (
                              <Option key={org.id} value={org.id}>
                                {org.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item
                          name="assigneeUserId"
                          label={
                            <div>
                              <div>Assignee</div>
                              <div style={{ color: 'gray', fontSize: '12px' }}>
                                The person that will receive this task
                              </div>
                            </div>
                          }
                          colon={false}
                        >
                          <Select
                            placeholder="Select Assignee"
                            showSearch
                            loading={basicUsersDetailsLoading}
                            onFocus={() => fetchBasicUsers()}
                            onSearch={(searchText) => fetchBasicUsers(searchText)}
                            filterOption={(input, option) =>
                              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                              optionA.children
                                .toLowerCase()
                                .localeCompare(optionB.children.toLowerCase())
                            }
                            onChange={(value) => {
                              const collaborators = currentTask.collaborators || [];
                              const collaboratorIds = collaborators.map((user) => user.id);
                              const followers = currentTask.followers || [];
                              const followerIds = followers.map((user) => user.id);
                              if (currentTaskId && collaboratorIds.includes(value)) {
                                setShowCollaboratorModal(true);
                                setSelectedCollaboratorId(value);
                              } else if (currentTaskId && followerIds.includes(value)) {
                                setShowFollowerModal(true);
                                setSelectedFollowerId(value);
                              } else {
                                form.setFieldsValue({
                                  assigneeUserId: value,
                                });
                              }
                            }}
                            className="custom-select"
                          >
                            {assigneeUsers.map((user) => (
                              <Option key={user.id} value={user.id}>
                                {startCase(user.name || user.username)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
                <Row gutter={32}>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      label={
                        <div>
                          <div>Channel Partner</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            The partner this task is regarding
                          </div>
                        </div>
                      }
                      rules={
                        level !== 'PLATFORM'
                          ? [] // No rules when level is CHANNEL_PARTNER
                          : [
                              {
                                required: true,
                                message: 'Channel Partner is Required',
                              },
                            ]
                      }
                      colon={false}
                      name="channelPartnerId"
                    >
                      <Select
                        allowClear
                        placeholder={level !== 'PLATFORM' ? userOrgName : 'Select Partner'}
                        showSearch
                        disabled={level !== 'PLATFORM'}
                        loading={basicOrgsDetailsLoading}
                        // value={
                        //   level === "CHANNEL_PARTNER" ? userName : undefined
                        // }
                        onFocus={() => fetchChannelPartnerOrgs()}
                        onSearch={(searchText) => fetchChannelPartnerOrgs(searchText)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA, optionB) => {
                          return optionA.children
                            .toLowerCase()
                            .localeCompare(optionB.children.toLowerCase());
                        }}
                        onChange={() => {
                          form.setFieldsValue({ advertiserId: [] });
                        }}
                        className="custom-select"
                      >
                        {channelPartnerOrgs.map((org) => (
                          <Option key={org.id} value={org.id}>
                            {org.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                      name="advertiserId"
                      showLineNumbers
                      colon={false}
                      label={
                        <div>
                          <div>Advertiser</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            The advertiser this task is regarding
                          </div>
                        </div>
                      }
                      rules={[{ required: true, message: 'Advertiser is Required' }]}
                    >
                      <Select
                        placeholder="Select Advertiser"
                        showSearch
                        loading={basicOrgsDetailsLoading}
                        onFocus={() => fetchAdvertiserOrgs()}
                        onSearch={(searchText) => fetchAdvertiserOrgs(searchText)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA, optionB) =>
                          optionA.children
                            .toLowerCase()
                            .localeCompare(optionB.children.toLowerCase())
                        }
                        className="custom-select"
                      >
                        {advertiserOrgs.map((org) => (
                          <Option key={org.id} value={org.id}>
                            {org.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={32}>
                  <Col span={24}>
                    <Form.Item
                      name="taskDetails"
                      colon={false}
                      label={
                        <div>
                          <div>Task Details</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Details about this task
                          </div>
                        </div>
                      }
                    >
                      <div style={{ position: 'relative' }}>
                        <Row
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            position: 'relative',
                          }}
                        >
                          <FileDropPasteArea
                            onImagesAdded={(newImages) => {
                              setAttachedImages((prev) => [...prev, ...newImages]);
                              if (currentTaskId) {
                                setNewAttachedImages((prev) => [...prev, ...newImages]);
                              }
                            }}
                            onFilesAdded={(newFiles) => {
                              setAttachedFiles((prev) => [...prev, ...newFiles]);
                              if (currentTaskId) {
                                setNewAttachedFiles((prev) => [...prev, ...newFiles]);
                              }
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                padding: '5px',
                                borderBottom: '1px solid #d9d9d9',
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <Tooltip title="Bold" mouseEnterDelay={0.5}>
                                <Button
                                  icon={<BoldOutlined />}
                                  onClick={() =>
                                    insertText({
                                      before: '**',
                                      after: '**',
                                    })
                                  }
                                  type="text"
                                />
                              </Tooltip>
                              <Tooltip title="Italic" mouseEnterDelay={0.5}>
                                <Button
                                  icon={<ItalicOutlined />}
                                  onClick={() =>
                                    insertText({
                                      before: '*',
                                      after: '*',
                                    })
                                  }
                                  type="text"
                                />
                              </Tooltip>
                              <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                                <Button
                                  icon={<StrikethroughOutlined />}
                                  onClick={() =>
                                    insertText({
                                      before: '~~',
                                      after: '~~',
                                    })
                                  }
                                  type="text"
                                />
                              </Tooltip>
                              <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                                <Button
                                  icon={<OrderedListOutlined />}
                                  onClick={() =>
                                    insertText({
                                      transform: (selectedText, start) => {
                                        if (selectedText === '') {
                                          return {
                                            text: '1. ',
                                            newStart: start + 3,
                                            newEnd: start + 3,
                                          };
                                        }
                                        const lines = selectedText
                                          .split('\n')
                                          .filter((line) => line.trim() !== '');
                                        const numbered = lines
                                          .map((line, i) => `${i + 1}. ${line}`)
                                          .join('\n');
                                        return {
                                          text: numbered,
                                          newStart: start,
                                          newEnd: start + numbered.length,
                                        };
                                      },
                                    })
                                  }
                                  type="text"
                                />
                              </Tooltip>
                              <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                                <Button
                                  icon={<UnorderedListOutlined />}
                                  onClick={() =>
                                    insertText({
                                      transform: (selectedText, start) => {
                                        if (selectedText === '') {
                                          return {
                                            text: '- ',
                                            newStart: start + 2,
                                            newEnd: start + 2,
                                          };
                                        }
                                        const lines = selectedText
                                          .split('\n')
                                          .filter((line) => line.trim() !== '');
                                        const dashed = lines.map((line) => `- ${line}`).join('\n');
                                        return {
                                          text: dashed,
                                          newStart: start,
                                          newEnd: start + dashed.length,
                                        };
                                      },
                                    })
                                  }
                                  type="text"
                                />
                              </Tooltip>
                            </div>
                            {(((attachedFiles && attachedFiles.length > 0) ||
                              (attachedImages && attachedImages.length > 0)) && (
                              <>
                                <div
                                  style={{
                                    backgroundColor: '#fafafa',
                                    paddingInline: '8px',
                                    paddingBlockStart: '5px',
                                    paddingBlockEnd: '30px',
                                    border: '1px solid #eee',
                                    marginBottom: '8px',
                                  }}
                                >
                                  {/* Render tags inside the text box */}
                                  {attachedFiles && attachedFiles.length > 0 && (
                                    <Row
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        width: '100%',
                                        padding: '4px',
                                      }}
                                    >
                                      {attachedFiles.map((file, index) => (
                                        <Tag
                                          closable
                                          key={index}
                                          onClose={() => {
                                            if (currentTaskId) {
                                              handleFileDelete(file.id, 'file');
                                            } else {
                                              const updatedFiles = attachedFiles.filter(
                                                (_, i) => i !== index
                                              );
                                              setAttachedFiles(updatedFiles);
                                            }
                                          }}
                                          icon={<PaperClipOutlined />}
                                          style={{
                                            margin: 0,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            maxWidth: '100%',
                                            position: 'relative',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {file.name.length > 30
                                            ? `${file.name.slice(0, 30)}...`
                                            : file.name}
                                        </Tag>
                                      ))}
                                    </Row>
                                  )}
                                  {attachedImages && attachedImages.length > 0 && (
                                    <Row
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        width: '100%',
                                        padding: '4px',
                                      }}
                                    >
                                      {attachedImages.map((file, index) => (
                                        <Tag
                                          closable
                                          key={index}
                                          onClose={() => {
                                            if (currentTaskId) {
                                              handleFileDelete(file.id, 'image');
                                            } else {
                                              const updatedImages = attachedImages.filter(
                                                (_, i) => i !== index
                                              );
                                              setAttachedImages(updatedImages);
                                            }
                                          }}
                                          icon={<PictureOutlined />}
                                          style={{
                                            margin: 0,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            maxWidth: '100%',
                                            position: 'relative',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {file.name.length > 30
                                            ? `${file.name.slice(0, 30)}...`
                                            : file.name}
                                        </Tag>
                                      ))}
                                    </Row>
                                  )}
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                  <Form.Item name="taskDetails" noStyle>
                                    <Input.TextArea
                                      ref={textareaRef}
                                      onChange={(e) => {
                                        form.setFieldsValue({
                                          taskDetails: e.target.value,
                                        });
                                      }}
                                      onKeyDown={handleKeyDown}
                                      maxLength={3000}
                                      placeholder="Enter task details"
                                      style={{
                                        border: 'none',
                                        borderRadius: '10px',
                                        outline: 'none',
                                        resize: 'vertical',
                                        minHeight: 2,
                                        width: '100%',
                                        padding: '10px',
                                        boxShadow: 'none',
                                        marginTop: -30,
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                              </>
                            )) || (
                              <div style={{ flexGrow: 1 }}>
                                <Form.Item name="taskDetails" noStyle>
                                  <Input.TextArea
                                    ref={textareaRef}
                                    onChange={(e) => {
                                      form.setFieldsValue({
                                        taskDetails: e.target.value,
                                      });
                                    }}
                                    onKeyDown={handleKeyDown}
                                    maxLength={3000}
                                    placeholder="Enter task details"
                                    style={{
                                      border: 'none',
                                      boxShadow: 'none',
                                      height: 200,
                                    }}
                                    className="custom-textarea"
                                  />
                                </Form.Item>
                              </div>
                            )}

                            {/* Hidden files input */}
                            <input
                              type="file"
                              ref={fileInputRef}
                              style={{ display: 'none' }}
                              multiple
                              accept={allowedFileExtensions.join(', ')}
                              onChange={(file) => {
                                const newFiles = Array.from(file.target.files);
                                if (newFiles.length > 0) {
                                  setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);
                                  if (currentTaskId) {
                                    setNewAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);
                                  }
                                }
                              }}
                            />

                            <input
                              type="file"
                              ref={imageInputRef}
                              style={{ display: 'none' }}
                              multiple
                              accept={allowedImageExtensions.join(', ')}
                              onChange={async (image) => {
                                const files = Array.from(image.target.files).filter((file) =>
                                  file.type.startsWith('image/')
                                );
                                setAttachedImages((prevFiles) => [...prevFiles, ...files]);
                                if (currentTaskId) {
                                  setNewAttachedImages((prevFiles) => [...prevFiles, ...files]);
                                }
                              }}
                            />
                          </FileDropPasteArea>
                        </Row>
                      </div>

                      <Row style={{ paddingTop: '10px', gap: '10px' }}>
                        <Tooltip title="Attach file" mouseEnterDelay={0.5}>
                          <Button
                            onClick={() => {
                              fileInputRef.current.click();
                            }}
                            style={{
                              height: '35px',
                              width: '40px',
                              borderRadius: '5px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              display: 'flex',
                            }}
                          >
                            <PaperClipOutlined
                              style={{
                                fontSize: '17px',
                              }}
                            />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Attach image" mouseEnterDelay={0.5}>
                          <Button
                            onClick={() => {
                              imageInputRef.current.click();
                            }}
                            style={{
                              height: '35px',
                              width: '40px',
                              borderRadius: '5px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              display: 'flex',
                            }}
                          >
                            <PictureOutlined
                              style={{
                                fontSize: '17px',
                              }}
                            />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Code block" mouseEnterDelay={0.5}>
                          <Button
                            onClick={handleInsertCode}
                            style={{
                              height: '35px',
                              width: '40px',
                              borderRadius: '5px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              display: 'flex',
                            }}
                          >
                            <CodeOutlined
                              style={{
                                fontSize: '17px',
                              }}
                            />
                          </Button>
                        </Tooltip>
                      </Row>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* CAMPAIGNS SECTION */}
            {hasCampaigns && (
              <Col style={{ width: '100%', marginBlock: '3vh' }}>
                <Card
                  style={{ borderRadius: '7px', backgroundColor: '#f9fafb' }}
                  title={<Text style={{ fontSize: '18px', fontWeight: 'bold' }}>CAMPAIGNS</Text>}
                  headStyle={{ borderBottom: 'none' }}
                >
                <Row gutter={32}>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <div>
                          <div>Related Campaigns</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Link this task to one or more campaigns to track work and discussions in context
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <div
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          padding: '16px',
                          backgroundColor: 'white',
                          minHeight: '120px',
                        }}
                      >
                        <Button
                          onClick={() => setRelatedCampaignModal(true)}
                          type="dashed"
                          icon={<PaperClipOutlined />}
                          style={{
                            width: '100%',
                            height: '40px',
                            borderColor: '#3b82f6',
                            color: '#3b82f6',
                            fontSize: '14px',
                            fontWeight: '500',
                          }}
                        >
                          {relatedCampaigns && relatedCampaigns.length > 0
                            ? '+ Add More Campaigns'
                            : '+ Link to Campaign'}
                        </Button>

                        {relatedCampaigns && relatedCampaigns.length > 0 ? (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                              gap: '12px',
                              marginTop: '16px',
                            }}
                          >
                            {relatedCampaigns.map((campaign) => (
                              <div
                                key={campaign.id}
                                style={{
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  padding: '12px 16px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px',
                                  transition: 'all 0.2s',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                                  e.currentTarget.style.borderColor = '#3b82f6';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f8fafc';
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1 }}>
                                    <a
                                      href={`/campaigns/details/${campaign.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        fontWeight: '600',
                                        color: '#3b82f6',
                                        fontSize: '14px',
                                        marginBottom: '4px',
                                        lineHeight: '1.3',
                                        textDecoration: 'none',
                                        display: 'block',
                                        transition: 'color 0.2s',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#2563eb';
                                        e.currentTarget.style.textDecoration = 'underline';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#3b82f6';
                                        e.currentTarget.style.textDecoration = 'none';
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {campaign.name}
                                    </a>
                                    <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <CalendarOutlined style={{ fontSize: '11px' }} />
                                      {moment(campaign.startDate).format('MMM DD, YYYY')} - {moment(campaign.endDate).format('MMM DD, YYYY')}
                                    </div>
                                  </div>
                                  <Button
                                    type="text"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const updatedCampaigns = relatedCampaigns.filter(
                                        (c) => c.id !== campaign.id
                                      );
                                      setRelatedCampaigns(updatedCampaigns);
                                      form.setFieldsValue({ relatedCampaigns: updatedCampaigns });
                                    }}
                                    style={{
                                      color: '#ef4444',
                                      padding: '4px 8px',
                                      fontSize: '12px',
                                      height: 'auto',
                                      minHeight: '24px',
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              textAlign: 'center',
                              padding: '32px 16px',
                              color: '#94a3b8',
                              fontSize: '13px',
                              marginTop: '8px',
                            }}
                          >
                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>No campaigns linked</div>
                            <div style={{ fontSize: '12px' }}>Link this task to campaigns to enable threaded discussions</div>
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
            )} 

            {(!currentTaskId || currentTaskId === 'new') && (
              <Col span={24} style={{ marginTop: '16px' }}>
                <Checkbox
                  checked={createMoreTasks}
                  onChange={(e) => setCreateMoreTasks(e.target.checked)}
                  style={{ fontSize: '14px' }}
                >
                  Create more tasks (keep form fields after submitting)
                </Checkbox>
              </Col>
            )}

            {/* Action Button */}
            <Col
              span={24}
              style={{
                display: 'flex',
                justifyContent: 'right',
                gap: '2px',
                marginTop: '20px',
              }}
            >
              {level === 'PLATFORM' && (
                <Col style={{ paddingInline: '8px' }}>
                  <Button
                    onClick={() => history.goBack()}
                    style={{
                      fontSize: '15px',
                      height: '41px',
                      borderRadius: '5px',
                    }}
                  >
                    Back
                  </Button>
                </Col>
              )}
              <Button
                onClick={async () => {
                  setButtonDisabled(true);
                  try {
                    await handleSubmit();
                  } catch (error) {
                    if (error.errorFields) {
                      const errorMessages = error.errorFields
                        .map((field) => field.errors.join(', '))
                        .join(', ');
                      message.destroy();
                      message.error(errorMessages);
                    } else {
                      message.error(error.message || 'An error occurred');
                    }
                  } finally {
                    setButtonDisabled(false);
                  }
                }}
                style={{
                  fontSize: '15px',
                  height: '41px',
                  borderRadius: '5px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                }}
                disabled={buttonDisabled}
              >
                {buttonDisabled && <LoadingOutlined />}{' '}
                {currentTaskId && currentTaskId !== 'new' ? 'Update Task' : 'Create Task'}
              </Button>
            </Col>
          </Row>

          {taskDetailVisible && (
            <TaskDatePickerModal
              visible={taskDetailVisible}
              setVisible={setTaskDatePickerVisible}
              currentDueDateConfig={dueDateRecurringConfig}
              disabledDates={!selectedProjectId}
              onClose={(values) => {
                setDueDateRecurringConfig(values);
                form.setFieldsValue({ dueDate: values.dueDate });
                form.validateFields(['startDate', 'dueDate']);
              }}
            />
          )}

          {taskStartDatePickerVisible && (
            <TaskStartDatePickerModal
              visible={taskStartDatePickerVisible}
              setVisible={setTaskStartDatePickerVisible}
              currentStartDate={form.getFieldValue('startDate')}
              disabledDates={!selectedProjectId}
              onClose={(values) => {
                form.setFieldsValue({ startDate: values.startDate });
                form.validateFields(['startDate', 'dueDate']);
              }}
            />
          )}

          {/* Create hidden relatedCampaigns Form.Item Input */}
          <Form.Item name="relatedCampaigns">
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </PageContentLayout>

      {showCodeSnippetModal && (
        <TaskCodeSnippetModal
          visible={showCodeSnippetModal}
          setVisible={setShowCodeSnippetModal}
          onEmbedSnippet={(codeSnippet) => {
            setAttachedCodeSnippet(codeSnippet);
          }}
        />
      )}

      {showRelatedCampaignModal && (
        <TaskRelatedCampaignsModal
          level={level}
          visible={showRelatedCampaignModal}
          setVisible={setRelatedCampaignModal}
          channelPartnerOrgId={
            level === 'CHANNEL_PARTNER'
              ? currentUser.role.org.id
              : form.getFieldValue('channelPartnerId')
          }
          selectedCampaigns={relatedCampaigns}
          onAddSelections={(selectedCampaignOrders) => {
            setRelatedCampaigns(selectedCampaignOrders);
            form.setFieldsValue({ relatedCampaigns: selectedCampaignOrders });
          }}
        />
      )}

      {(showCollaboratorModal || showFollowerModal) && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setShowCollaboratorModal(false) || setShowFollowerModal(false)}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={async () => {
                if (showFollowerModal) {
                  try {
                    message.loading('Removing follower...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: {
                          followers: {
                            disconnect: [{ id: selectedFollowerId }],
                          },
                        },
                      },
                    });
                    message.destroy();
                    message.success('Follower removed successfully');
                    setShowFollowerModal(false);
                  } catch (error) {
                    message.error(`Failed to remove follower: ${error.message}`);
                  }
                  return;
                } else {
                  try {
                    message.loading('Removing collaborator...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: {
                          collaborators: {
                            disconnect: [{ id: selectedCollaboratorId }],
                          },
                        },
                      },
                    });
                    message.destroy();
                    message.success('Collaborator removed successfully');
                    setShowCollaboratorModal(false);
                  } catch (error) {
                    message.error(`Failed to remove collaborator: ${error.message}`);
                  }
                }
              }}
            >
              {updateTaskLoading && <LoadingOutlined />} Remove
            </Button>,
          ]}
          title={showCollaboratorModal ? 'Remove Collaborator' : 'Remove Follower'}
          visible={showCollaboratorModal || showFollowerModal}
        >
          <p>
            {showCollaboratorModal
              ? 'This user is already a collaborator. Do you want to promote them as an assignee and remove them from collaborators?'
              : 'This user is a follower. Do you want to promote them as an assignee and remove them from followers?'}
          </p>
        </Modal>
      )}
    </>
  );
};

export default withRouter(TaskForm);
